import React, { useState } from 'react';

// Api
import { procurementReportApi } from '@/main/api';

// Services
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';

// Types
import { ProcurementReportResponse, SearchGridShippingCostResponse } from '@/main/models/response';
import { ProcurementReportRequest, SearchGridShippingAndBillingRequest } from '@/main/models/request';

// Components
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { KOGTableRow } from '@/main/components/shared/boxGrid/styles';


// Components/Styles Local

import * as S from '../../styles'
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import { makeStyles } from '@mui/styles';
import { IGridFiltersData } from '../../../components/filters/boxFiltersComponentes';

interface IOrderReportGrid {
  formFilters: SearchGridShippingAndBillingRequest | null;
  gridData: IGridFiltersData;
  hasShowMore: boolean;
  setHasShowMore: React.Dispatch<React.SetStateAction<boolean>>;
  setGridData: React.Dispatch<React.SetStateAction<IGridFiltersData>>;
  pageIndex: PaginationEnum;
  setPageIndex: React.Dispatch<React.SetStateAction<PaginationEnum>>;
  isLoadingInfiniteScroll: boolean;
  setIsLoadingInfiniteScroll: React.Dispatch<React.SetStateAction<boolean>>;
}
function ShippingCostGrid({
  formFilters,
  hasShowMore,
  gridData,
  setHasShowMore,
  setGridData,
  pageIndex,
  setPageIndex,
  isLoadingInfiniteScroll,
  setIsLoadingInfiniteScroll
}: IOrderReportGrid) {

  // ASYNC FUNCTIONS
  function onLoadInfiniteScroll(request: SearchGridShippingAndBillingRequest) {
    setIsLoadingInfiniteScroll(true);

    procurementReportApi
      .searchGridShippingCost(request)
      .then(response => {
        let resultData: SearchGridShippingCostResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize10 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);
    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      const updatedFormFilters = {
        ...formFiltersOnLoadSearchGrid,
        pageIndex: index
      };
      onLoadInfiniteScroll(updatedFormFilters);
    }
  }

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  // CONSTANTS LAYOUT
  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );
  const rowHeaders = [
    getTranslateByKey('information.insurer'),
    getTranslateByKey('information.bodyshop'),
    getTranslateByKey('information.claimNr'),
    getTranslateByKey('information.vehicle'),
    getTranslateByKey('information.supplier'),
    getTranslateByKey('information.orderNr'),
    getTranslateByKey('packingTrackingHistory.trackingNumber'),
    getTranslateByKey('partdetail.packageContent'),
    getTranslateByKey('information.shippingcosts'),
    getTranslateByKey('information.packageType')
  ];

  const useStylesTooltip = makeStyles({
    popperCustom: { marginLeft: 15 }
  });

  const classesTooltip = useStylesTooltip();

  function renderTextGrid(text: string | number) {
    if (!isNullUndefined(text)) {
      return (
        <TooltipComponent
          title={text}
          stylePopper={classesTooltip.popperCustom}
        >
          <S.TableGrid>
            {text}
          </S.TableGrid>
        </TooltipComponent>
      )
    } else {
      return '';
    }
  }

  function renderTextGridPackageContents(text: string | number) {
    if (!isNullUndefined(text)) {
      const contentsArray = text.toString().split(',');
      return (
        <TooltipComponent
          title={
            contentsArray.map((content, index) => (
              <React.Fragment key={index}>
                {content.trim()}
                {index !== contentsArray.length - 1 && (<>,<br /></>)}
              </React.Fragment>
            ))
          }
          stylePopper={classesTooltip.popperCustom}
        >
          <S.TableGrid>
            {text}
          </S.TableGrid>
        </TooltipComponent>
      )
    } else {
      return '';
    }
  }

  return (
    <BoxGrid
      title={titleGrid}
      rowHeaders={rowHeaders}
      infiniteScrollConfig={{
        dataLength: gridData.data.length,
        fetchData: () => onLoadSearchGrid(),
        hasMore: hasShowMore,
        isLoading: isLoadingInfiniteScroll
      }}>
      {gridData.data.map(part => (
        <KOGTableRow
          key={part.insurerName}
          id={`id-shippingCost-tableRow-result-${part.insurerName}`}>
          <S.TableCellGridLarge>
            {renderTextGrid(part.insurerName)}
          </S.TableCellGridLarge>

          <S.TableCellGridLarge>
            {renderTextGrid(part.bodyShopName)}
          </S.TableCellGridLarge>

          <S.TableCellGridLarge>
            {renderTextGrid(part.claimNr)}
          </S.TableCellGridLarge>

          <S.TableCellGridLarge>
            {renderTextGrid(part.vehicle)}
          </S.TableCellGridLarge>

          <S.TableCellGridLarge>
            {renderTextGrid(part.supplierName)}
          </S.TableCellGridLarge>

          <S.TableCellGridOrderNr>
            {renderTextGrid(part.orderNr)}
          </S.TableCellGridOrderNr>

          <S.TableCellGridLarge>
            {renderTextGrid(part.trackingNumber)}
          </S.TableCellGridLarge>

          <S.TableCellGridLarge>
            {renderTextGridPackageContents(part.packageContents)}
          </S.TableCellGridLarge>

          <S.TableCellGridLarge>
            {renderTextGrid(part.shippingCost)}
          </S.TableCellGridLarge>
          <S.TableCellGridLarge>
            {renderTextGrid(part.packageType)}
          </S.TableCellGridLarge>
        </KOGTableRow>
      ))}
    </BoxGrid>
  );

}

export default ShippingCostGrid;